// jungleCampImages.ts
// Community Dragon CDN links for jungle camp images

export const jungleCampImages = {
  // Blue Sentinel (Blue Buff)
  blue: "https://raw.communitydragon.org/latest/game/assets/characters/sru_blue/hud/bluesentinel_square.png",
  
  // Red Brambleback (Red Buff)
  red: "https://raw.communitydragon.org/latest/game/assets/characters/sru_red/hud/brambleback_square.png",
  
  // Gromp
  gromp: "https://raw.communitydragon.org/latest/game/assets/characters/sru_gromp/hud/gromp_square.png",
  
  // Wolves (Greater Murkwolf + 2 Small Murkwolves)
  wolves: "https://raw.communitydragon.org/latest/game/assets/characters/sru_murkwolf/hud/greatermurkwolf_square.png",
  wolves_small: "https://raw.communitydragon.org/latest/game/assets/characters/sru_murkwolfmini/hud/murkwolf_square.png",
  
  // Raptors (Crimson Raptor + 5 Small Raptors)
  raptors: "https://raw.communitydragon.org/latest/game/assets/characters/sru_razorbeak/hud/razorbeak_square.png",
  raptors_small: "https://raw.communitydragon.org/latest/game/assets/characters/sru_razorbeakmini/hud/razorbeakmini_square.png",
  
  // Krugs (Ancient Krug + Krug + 4 Mini Krugs)
  krugs: "https://raw.communitydragon.org/latest/game/assets/characters/sru_krug/hud/ancientkrug_square.png",
  krugs_medium: "https://raw.communitydragon.org/latest/game/assets/characters/sru_krugmini/hud/krug_square.png",
  krugs_small: "https://raw.communitydragon.org/latest/game/assets/characters/sru_krugmini/hud/krug_square.png",
  
  // Rift Scuttler
  scuttle: "https://raw.communitydragon.org/latest/game/assets/characters/sru_crab/hud/crab_square_0.png",
  
  // Epic Monsters
  dragon: "https://raw.communitydragon.org/latest/game/assets/characters/sru_dragon/hud/dragon_square.png",
  dragon_cloud: "https://raw.communitydragon.org/latest/game/assets/characters/sru_dragon_air/hud/dragon_air_square.png",
  dragon_ocean: "https://raw.communitydragon.org/latest/game/assets/characters/sru_dragon_water/hud/dragon_square_water.png",
  dragon_mountain: "https://raw.communitydragon.org/latest/game/assets/characters/sru_dragon_earth/hud/dragon_square_earth.png",
  dragon_infernal: "https://raw.communitydragon.org/latest/game/assets/characters/sru_dragon_fire/hud/dragon_square_fire.png",
  dragon_hextech: "https://raw.communitydragon.org/latest/game/assets/characters/sru_dragon_hextech/hud/hextechdrake_square.png",
  dragon_chemtech: "https://raw.communitydragon.org/latest/game/assets/characters/sru_dragon_hextech/hud/icons2d/dragon_square_hextech.png",
  dragon_elder: "https://raw.communitydragon.org/latest/game/assets/characters/sru_dragon_elder/hud/dragon_square_elder.png",
  
  // Baron Nashor
  baron: "https://raw.communitydragon.org/latest/game/assets/characters/sru_baron/hud/baron_square.png",
  
  // Rift Herald
  herald: "https://raw.communitydragon.org/latest/game/assets/characters/sru_riftherald/hud/sruriftherald_square.png",
  
  // Voidgrubs
  voidgrubs: "https://raw.communitydragon.org/latest/game/assets/characters/sru_horde/hud/sru_voidgrub_square.png",
  
  // Atakhan (both variants)
  atakhan_voracious: "https://raw.communitydragon.org/latest/game/assets/characters/sru_atakhan/hud/atakhan_r_square_128.png",

  minion_cannon_blue: "https://raw.communitydragon.org/latest/game/assets/characters/sru_orderminionsiege/hud/bluemechcannon_square.png",
  minion_cannon_red: "https://raw.communitydragon.org/latest/game/assets/characters/sru_orderminionsiege/hud/redmechcannon_square.png",

  minion_caster_blue: "https://raw.communitydragon.org/latest/game/assets/characters/sru_orderminionranged/hud/bluerange_square.png",
  minion_caster_red: "https://raw.communitydragon.org/latest/game/assets/characters/sru_orderminionranged/hud/redrange_square.png",

  minion_melee_blue: "https://raw.communitydragon.org/latest/game/assets/characters/sru_orderminionmelee/hud/bluemelee_square.png",
  minion_melee_red: "https://raw.communitydragon.org/latest/game/assets/characters/sru_orderminionmelee/hud/redmelee_square.png",
};

export type JungleCampId = keyof typeof jungleCampImages;

/**
 * Get the image URL for a jungle camp by its base type
 * @param baseType - The base camp type (blue, red, gromp, wolves, raptors, krugs, scuttle)
 * @returns The image URL for the camp
 */
export function getCampImage(baseType: string): string {
  return jungleCampImages[baseType as JungleCampId] || jungleCampImages.scuttle;
}

/**
 * Get the image URL for an objective
 * @param objectiveId - The objective ID (dragon, baron, herald, voidgrubs, atakhan-top, atakhan-bot)
 * @returns The image URL for the objective
 */
export function getObjectiveImage(objectiveId: string): string {
  if (objectiveId === 'dragon') return jungleCampImages.dragon;
  if (objectiveId === 'baron') return jungleCampImages.baron;
  if (objectiveId === 'herald') return jungleCampImages.herald;
  if (objectiveId === 'voidgrubs') return jungleCampImages.voidgrubs;
  if (objectiveId.startsWith('atakhan')) return jungleCampImages.atakhan_voracious;
  return jungleCampImages.scuttle; // fallback
}

/**
 * Get minion images for a wave
 * @param waveNumber - The wave number
 * @param team - 'blue' or 'red' team
 * @returns Array of minion image URLs for the wave
 */
export function getWaveMinionImages(waveNumber: number, team: 'blue' | 'red'): string[] {
  const hasCannonMinion = waveNumber >= 3 && waveNumber % 3 === 0;
  const images: string[] = [];
  
  // 3 melee minions
  for (let i = 0; i < 3; i++) {
    images.push(jungleCampImages[`minion_melee_${team}` as JungleCampId]);
  }
  
  // 3 caster minions
  for (let i = 0; i < 3; i++) {
    images.push(jungleCampImages[`minion_caster_${team}` as JungleCampId]);
  }
  
  // 1 cannon minion (if applicable)
  if (hasCannonMinion) {
    images.push(jungleCampImages[`minion_cannon_${team}` as JungleCampId]);
  }
  
  return images;
}

